import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCompactViewport } from "../hooks/useCompactViewport";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
function resolvePalette(isDark) {
    // MENU_STANDARD: theme tokens first (flip with light/dark/preset themes),
    // previous hardcoded rgba kept as fallbacks so default themes look identical.
    if (isDark) {
        return {
            backdrop: "rgba(15, 23, 42, 0.12)",
            panel: "var(--vnsc-bg-primary-rgba, rgba(15, 23, 42, 0.96))",
            border: "var(--vnsc-border, rgba(148, 163, 184, 0.22))",
            text: "var(--vnsc-text-primary, #f8fafc)",
            subtleText: "var(--vnsc-text-secondary, rgba(248, 250, 252, 0.7))",
            hover: "rgba(148, 163, 184, 0.12)",
            selectedBg: "color-mix(in srgb, var(--vnsc-primary, #2962ff) 15%, transparent)",
            selectedText: "var(--vnsc-primary, #2962ff)",
            danger: "#fca5a5",
        };
    }
    return {
        backdrop: "rgba(15, 23, 42, 0.08)",
        panel: "var(--vnsc-bg-primary-rgba, rgba(255, 255, 255, 0.98))",
        border: "var(--vnsc-border, rgba(15, 23, 42, 0.14))",
        text: "var(--vnsc-text-primary, #0f172a)",
        subtleText: "var(--vnsc-text-secondary, rgba(15, 23, 42, 0.64))",
        hover: "rgba(59, 130, 246, 0.08)",
        selectedBg: "color-mix(in srgb, var(--vnsc-primary, #2962ff) 15%, transparent)",
        selectedText: "var(--vnsc-primary, #2962ff)",
        danger: "#b91c1c",
    };
}
export function DrawingContextMenu({ ariaLabel, items, onClose, position, title, isDark, positionSpace = "container", flipBounds }) {
    const isCompactViewport = useCompactViewport();
    const panelRef = useRef(null);
    // P2-V-01 (reworked): keep the cursor-anchored panel inside its bounds
    // (e.g. Y-axis menu at the right edge). The correction derives from the
    // RAW anchor + measured panel SIZE (never the already-shifted rect), so it
    // is idempotent across re-renders — no reset effect, no open/close flash,
    // no feedback oscillation while live data re-renders the parent.
    const [flipped, setFlipped] = useState(null);
    useLayoutEffect(() => {
        if (!position || isCompactViewport || typeof window === "undefined")
            return;
        const panel = panelRef.current;
        if (!panel)
            return;
        const rect = panel.getBoundingClientRect();
        if (!(rect.width > 0) || !(rect.height > 0))
            return;
        const margin = 8;
        let boundRight;
        let boundBottom;
        if (positionSpace === "viewport") {
            boundRight = window.innerWidth;
            boundBottom = window.innerHeight;
            // CHART_CLAMP: prefer the owning chart's box when provided.
            if (typeof flipBounds?.right === "number" && Number.isFinite(flipBounds.right)) {
                boundRight = Math.min(boundRight, flipBounds.right);
            }
            if (typeof flipBounds?.bottom === "number" && Number.isFinite(flipBounds.bottom)) {
                boundBottom = Math.min(boundBottom, flipBounds.bottom);
            }
        }
        else {
            // offsetParent is the absolutely-positioned overlay ancestor; fall back
            // to the direct parent so jsdom/unit tests can still exercise the math.
            const container = panel.offsetParent ?? panel.parentElement ?? null;
            if (!container)
                return;
            const bounds = container.getBoundingClientRect();
            if (!(bounds.width > 0) || !(bounds.height > 0))
                return;
            boundRight = bounds.right;
            boundBottom = bounds.bottom;
        }
        const dx = Math.min(0, boundRight - (position.x + rect.width) - margin);
        const dy = Math.min(0, boundBottom - (position.y + rect.height) - margin);
        setFlipped((prev) => {
            const next = dx !== 0 || dy !== 0 ? { x: position.x + dx, y: position.y + dy } : null;
            if (prev?.x === next?.x && prev?.y === next?.y)
                return prev;
            return next;
        });
    }, [position?.x, position?.y, positionSpace, flipBounds?.right, flipBounds?.bottom, isCompactViewport, items.length]);
    useEffect(() => {
        if (!position || typeof window === "undefined") {
            return;
        }
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose, position]);
    if (!position || items.length === 0) {
        return null;
    }
    const palette = resolvePalette(isDark);
    const overlayStyle = {
        position: positionSpace === "viewport" ? "fixed" : "absolute",
        inset: 0,
        // P5: design token (was a hardcoded 40 that tied with pane headers).
        zIndex: "var(--vnsc-z-drawing-context-menu, 80)",
        pointerEvents: "auto",
    };
    const backdropStyle = {
        position: "absolute",
        inset: 0,
        background: palette.backdrop,
    };
    const panelStyle = {
        position: positionSpace === "viewport" ? "fixed" : "absolute",
        left: isCompactViewport ? 8 : (flipped?.x ?? position.x),
        right: isCompactViewport ? 8 : undefined,
        top: isCompactViewport ? "auto" : (flipped?.y ?? position.y),
        bottom: isCompactViewport ? 8 : undefined,
        minWidth: isCompactViewport ? 0 : 200,
        maxWidth: isCompactViewport ? "none" : 280,
        padding: isCompactViewport ? "12px 14px 14px" : 6,
        borderRadius: isCompactViewport ? 22 : 8,
        border: `1px solid ${palette.border}`,
        background: palette.panel,
        boxShadow: isCompactViewport ? "0 -18px 36px rgba(15, 23, 42, 0.2)" : "0 8px 24px rgba(0, 0, 0, 0.3)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        color: palette.text,
        width: isCompactViewport ? "auto" : undefined,
        maxHeight: isCompactViewport ? "min(48vh, 420px)" : undefined,
        overflowY: isCompactViewport ? "auto" : undefined,
    };
    const titleStyle = {
        margin: "0 0 8px",
        padding: "0 2px 4px",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: palette.subtleText,
    };
    const itemListStyle = {
        display: "flex",
        flexDirection: "column",
        gap: isCompactViewport ? 8 : 2,
    };
    const baseItemBackground = (item) => {
        if (item.danger)
            return "rgba(220, 38, 38, 0.08)";
        if (item.selected)
            return palette.selectedBg;
        return "transparent";
    };
    const baseItemColor = (item) => {
        if (item.disabled)
            return palette.subtleText;
        if (item.danger)
            return palette.danger;
        if (item.selected)
            return palette.selectedText;
        return palette.text;
    };
    return (_jsxs("div", { style: overlayStyle, children: [_jsx("button", { type: "button", "aria-label": ariaLabel, style: backdropStyle, onMouseDown: onClose }), _jsxs("div", { ref: panelRef, style: panelStyle, role: "menu", "aria-label": ariaLabel, onMouseDown: (event) => event.stopPropagation(), children: [title && _jsx("div", { style: titleStyle, children: title }), _jsx("div", { style: itemListStyle, children: items.map((item) => (item.type === "separator" ? (_jsx("div", { role: "separator", "aria-orientation": "horizontal", style: {
                                height: 1,
                                margin: isCompactViewport ? "4px 2px" : "4px 10px",
                                background: palette.border,
                                flexShrink: 0,
                            } }, item.key)) : (_jsx("button", { type: "button", role: "menuitem", disabled: item.disabled, "aria-disabled": item.disabled, "aria-current": item.selected ? true : undefined, onClick: () => {
                                if (item.disabled) {
                                    return;
                                }
                                item.onSelect();
                                onClose();
                            }, style: {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                padding: isCompactViewport ? "10px 12px" : "7px 10px",
                                borderRadius: isCompactViewport ? 12 : 6,
                                border: "none",
                                background: baseItemBackground(item),
                                color: baseItemColor(item),
                                fontSize: isCompactViewport ? 13 : 12,
                                fontWeight: item.selected ? 600 : 500,
                                lineHeight: 1.2,
                                cursor: item.disabled ? "not-allowed" : "pointer",
                                transition: "background 160ms ease, transform 160ms ease",
                                textAlign: "left",
                            }, onMouseEnter: (event) => {
                                if (!item.disabled) {
                                    event.currentTarget.style.background = item.danger ? "rgba(220, 38, 38, 0.12)" : palette.hover;
                                }
                            }, onMouseLeave: (event) => {
                                event.currentTarget.style.background = baseItemBackground(item);
                            }, children: _jsx("span", { children: item.label }) }, item.key)))) })] })] }));
}
